// src/pages/CollectionOrders/subpages/CollectionOrderEdit.tsx
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../../components/default/DefaultCard';
import LazyLoading from '../../../components/LazyLoading';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import { Tab, Tabs } from 'react-bootstrap';

import useCollectionOrderService from '../../../app/services/hooks/useCollectionOrderService';
import { AppContext } from '../../../contexts/AppContext';
import OrderHistoryTab from '../components/tables/OrderHistoryTab';

import {
  CollectionOrder,
  CollectionOrderUpdatePayload,
  CollectionOrderBatchRow,
  CollectionOrderCollectorRow,
} from '../../../app/models/CollectionOrder';

import { formatDateToInputDate } from '../../../helpers';
import CollectionOrderHeaderForm from '../components/forms/CollectionOrderHeaderForm';
import OrderBatchesTab from '../components/tables/OrderBatchesTab';
import OrderCollectorsTab from '../components/tables/OrderCollectorsTab';
import OrderIncidentsTab from '../components/tables/OrderIncidentsTab';

type RouterParams = { id: string };
type SelectOpt = { id: number | string; name: string };

const CollectionOrdersEdit: React.FC = () => {
  const { id } = useParams<RouterParams>();
  const orderId = Number(id);

  const { showLoading, hideLoading } = useContext(AppContext);
  const {
    editCollectionOrder,
    updateCollectionOrder,
    fetchingEditCollectionOrder,
    fetchingUpdateCollectionOrder,
  } = useCollectionOrderService();

  // REFS para evitar bucles
  const editRef = useRef(editCollectionOrder);
  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);
  useEffect(() => { editRef.current = editCollectionOrder; }, [editCollectionOrder]);
  useEffect(() => { showLoadingRef.current = showLoading; }, [showLoading]);
  useEffect(() => { hideLoadingRef.current = hideLoading; }, [hideLoading]);
  const [reloadTick, setReloadTick] = useState(0);

  const [verifying, setVerifying] = useState<boolean>(true);

  const [order, setOrder] = useState<CollectionOrder>({
    status: 'CREATED',
    is_active: true,
    collectors: [],
    batches: [],
  });

  const [societies, setSocieties] = useState<SelectOpt[]>([]);
  const [projects, setProjects] = useState<SelectOpt[]>([]);
  const [units, setUnits] = useState<SelectOpt[]>([]);
  const [statuses, setStatuses] = useState<SelectOpt[]>([]);
  const [errorFields, setErrorFields] = useState<any>();

  const load = useCallback((oid: number) => {
    showLoadingRef.current?.('loading', 'Cargando orden...');
    editRef.current(oid, {
      onSuccess: (resp: ServiceResponse) => {
        const o = resp.data.collection_order as CollectionOrder;
        setOrder({
          ...o,
          collectors: o.collectors ?? [],
          batches: o.batches ?? [],
        });
        setSocieties(resp.data.societies || []);
        setProjects(resp.data.projects || []);
        setUnits(resp.data.units || []);
        setStatuses(resp.data.status || []);
        hideLoadingRef.current?.();
        setVerifying(false);
      },
      onError: (resp: ServiceResponse) => {
        hideLoadingRef.current?.();
        toast.error(resp.message, { autoClose: 2500 });
        setVerifying(false);
      }
    });
  }, []);

  useEffect(() => { if (Number.isFinite(orderId)) load(orderId); }, [orderId, load]);

  const onSaveHeader = (payload: CollectionOrderUpdatePayload) => {
    showLoadingRef.current?.('loading', 'Actualizando orden...');
    updateCollectionOrder(orderId, payload, {
      onSuccess: (resp: ServiceResponse) => {
        hideLoadingRef.current?.();
        toast.success(resp.message);
        setErrorFields(undefined);
        load(orderId);
        setReloadTick(prev => prev + 1);
      },
      onError: (resp: ServiceResponse) => {
        hideLoadingRef.current?.();
        toast.error(resp.message);
      },
      onFieldError: (errs: ServiceResponse) => {
        hideLoadingRef.current?.();
        setErrorFields(errs.data);
        
        // Mostrar errores generales (non_field_errors)
        if (errs.data?.non_field_errors) {
          const errors = errs.data.non_field_errors;
          if (Array.isArray(errors)) {
            errors.forEach((error: string) => {
              toast.error(error, { autoClose: 6000 });
            });
          } else if (typeof errors === 'string') {
            toast.error(errors, { autoClose: 6000 });
          }
        } else {
          // Si hay errores de campo pero no non_field_errors
          toast.warning('Por favor revise los campos marcados en rojo.', { autoClose: 3000 });
        }
      }
    });
  };

  const breadcrumbs: BreadcrumbsItem[] = [
    { name: 'Órdenes de Recolección', url: '/collection-orders', isActive: false },
    { name: `Editar Orden de Recolección #${orderId}`, url: `/collection-orders/${orderId}/edit`, isActive: true },
  ];

  const formatDate = (d?: string | null) => {
    if (!d) return '';
    try { const date = new Date(d); return Number.isNaN(date.getTime()) ? (d as string) : date.toLocaleDateString(); }
    catch { return d as string; }
  };

  const createdByName =
    (order as any).created_by_name ||
    (order as any).created_by_fullname ||
    [ (order as any)?.created_by?.first_name, (order as any)?.created_by?.last_name ].filter(Boolean).join(' ') ||
    (order as any)?.created_by?.username || '';

  const updatedByName =
    (order as any).updated_by_name ||
    (order as any).updated_by_fullname ||
    [ (order as any)?.updated_by?.first_name, (order as any)?.updated_by?.last_name ].filter(Boolean).join(' ') ||
    (order as any)?.updated_by?.username || '';

  if (verifying) {
    return (
      <DefaultCard>
        <LazyLoading height={300} />
      </DefaultCard>
    );
  }

  return (
    <>
      <Breadcrumbs pageSection={`Editar Orden #${orderId}`} breadcrumbs={breadcrumbs} />

      {/* Tabs superiores */}
      <Tabs defaultActiveKey="general" id="co-edit-top-tabs" className="mb-3">
        {/* === TAB 1: Datos Generales === */}
        <Tab eventKey="general" title="Datos Generales">

          {/* Fila con 2 contenedores separados */}
          <div className="row">
            {/* Contenedor izquierdo: Datos Generales */}
            <div className="col-12 col-lg-8">
              <DefaultCard>
                <h5 className="mb-3">Datos Generales</h5>
                <CollectionOrderHeaderForm
                  fetching={!!(fetchingEditCollectionOrder || fetchingUpdateCollectionOrder)}
                  order={order}
                  setOrder={setOrder}
                  societies={societies}
                  projects={projects}
                  units={units}
                  statuses={statuses}
                  errorFields={errorFields}
                  onCancel={() => window.history.back()}
                  onSave={(o) => {
                    const toYMD = (d: any) => {
                      if (!d) return undefined;
                      if (typeof d === 'string') return d.slice(0, 10);
                      if (d instanceof Date && !isNaN(d.getTime())) return formatDateToInputDate(d);
                      return undefined;
                    };

                    const programDate = toYMD((o as any).program_date);
                    const startDate = (o as any).start_date ?? undefined; // conservar fecha+hora

                    const base: CollectionOrderUpdatePayload = {
                      society_id: o.society_id ?? null,
                      project_id: o.project_id ?? null,
                      ...(programDate !== undefined ? { program_date: programDate } : {}),
                      ...(startDate !== undefined ? { start_date: startDate } : {}),
                      status: o.status as any,
                      is_active: o.is_active,
                      planned_quantity: o.planned_quantity ?? null,
                      planned_unit_id: o.planned_unit_id ?? null,
                    };

                    const maybeEnd = (o as any).end_date ?? undefined;

                    //No permitir FINALIZED sin end_date
                    if (o.status === 'FINALIZED' && !maybeEnd) {
                      toast.error('Para finalizar debe indicar la Fecha Fin.');
                      return;
                    }

                    // Enviar end_date sólo si FINALIZED (con valor)
                    const payload: CollectionOrderUpdatePayload =
                      o.status === 'FINALIZED'
                        ? { ...base, end_date: maybeEnd! }
                        : base;

                    onSaveHeader(payload);
                  }}
                />
              </DefaultCard>
            </div>

            {/* Contenedor derecho: Datos del creador */}
            <div className="col-12 col-lg-4">
              <DefaultCard>
                <h5 className="mb-3">Datos del creador</h5>
                <div className="mb-2">
                  <label className="form-label">Creado por</label>
                  <input className="form-control" value={createdByName} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fecha de creación</label>
                  <input className="form-control" value={formatDate((order as any).created_at)} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Editado por</label>
                  <input className="form-control" value={updatedByName} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fecha de actualización</label>
                  <input className="form-control" value={formatDate((order as any).updated_at)} disabled />
                </div>
              </DefaultCard>
            </div>
          </div>

          {/* Contenedor inferior: pestañas con tablas (Lotes / Usuarios) */}
          <DefaultCard>
            <div style={{ minHeight: 260 }}>
              <Tabs defaultActiveKey="batches" id="collection-order-subtabs" className="mb-0">
              <Tab eventKey="batches" title="Lotes asignados">
                <div className="col-12 pt-2">
                  <OrderBatchesTab
                    orderId={orderId}
                    rows={(order.batches as CollectionOrderBatchRow[]) || []}
                    onChanged={() => load(orderId)}
                    readOnly={order.status === 'FINALIZED'}
                  />
                </div>
              </Tab>
              <Tab eventKey="collectors" title="Usuarios asignados">
                <div className="col-12 pt-2">
                  <OrderCollectorsTab
                    orderId={orderId}
                    rows={(order.collectors as CollectionOrderCollectorRow[]) || []}
                    onChanged={() => load(orderId)}
                  />
                </div>
              </Tab>
            </Tabs>
            </div>
          </DefaultCard>
        </Tab>

        {/* === TAB 2: Incidentes === */}
        <Tab eventKey="incidents" title="Incidentes">
          <OrderIncidentsTab
              key={reloadTick}         
              orderId={orderId}
              onChanged={() => setReloadTick(prev => prev + 1)}
          />
        </Tab>

        {/* === TAB 3: Historial === */}
        <Tab eventKey="history" title="Historial">
          <OrderHistoryTab key={reloadTick} orderId={orderId} />
        </Tab>
      </Tabs>
    </>
  );
};

export default CollectionOrdersEdit;
